import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { categoriesAPI } from '../api/client'
import { useToast } from '../components/Toast'
import {
  FolderTree,
  Link,
  Unlink,
  CheckCircle,
  AlertCircle,
  ChevronDown,
  Loader2,
  RefreshCw,
  Search,
  Plus,
  X
} from 'lucide-react'

function StatCard({ icon: Icon, value, label, color = 'primary' }) {
  const colorClasses = {
    primary: 'text-primary',
    success: 'text-success',
    accent: 'text-accent',
    secondary: 'text-secondary',
    warning: 'text-yellow-400',
  }

  return (
    <div className="stat-card">
      <div className="flex items-center justify-between mb-4">
        <Icon className={`w-8 h-8 ${colorClasses[color]}`} />
      </div>
      <div className={`stat-value ${colorClasses[color]}`}>{value}</div>
      <div className="stat-label">{label}</div>
    </div>
  )
}

function UnmappedCategoryRow({ category, aggCategories, onMap, onEnrich, onCreateAndMap }) {
  const [selectedCategoryId, setSelectedCategoryId] = useState('')
  const [isMapping, setIsMapping] = useState(false)
  const [isEnriching, setIsEnriching] = useState(false)
  const [enrichedData, setEnrichedData] = useState(null)
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [newCategoryName, setNewCategoryName] = useState('')
  const [isCreating, setIsCreating] = useState(false)

  const displayName = enrichedData?.title || category.source_category_name
  const description = enrichedData?.description

  const handleMap = async () => {
    if (!selectedCategoryId) return
    setIsMapping(true)
    try {
      await onMap({
        source_id: category.source_id,
        source_category_slug: category.source_category_slug,
        agg_category_id: parseInt(selectedCategoryId)
      })
    } finally {
      setIsMapping(false)
    }
  }

  const handleEnrich = async () => {
    setIsEnriching(true)
    try {
      const data = await onEnrich(category.id)
      setEnrichedData(data)
      if (data.title) {
        setNewCategoryName(data.title)
      }
    } finally {
      setIsEnriching(false)
    }
  }

  const handleCreate = async () => {
    if (!newCategoryName.trim()) return
    setIsCreating(true)
    try {
      await onCreateAndMap({
        source_mapping_id: category.id,
        name_ru: newCategoryName.trim()
      })
      setShowCreateForm(false)
    } finally {
      setIsCreating(false)
    }
  }

  return (
    <tr className="border-b border-white/10 hover:bg-white/5">
      <td className="p-3">
        <div className="font-medium">{category.source_category_slug}</div>
        {displayName && (
          <div className="text-sm text-primary">{displayName}</div>
        )}
        {description && (
          <div className="text-xs opacity-50 mt-1 max-w-md truncate" title={description}>
            {description}
          </div>
        )}
      </td>
      <td className="p-3 text-sm opacity-60">{category.source_name}</td>
      <td className="p-3 text-center font-medium">{category.images_count}</td>
      <td className="p-3 text-center">
        {category.quality_images_count !== undefined && (
          <span className={category.quality_images_count > 0 ? 'text-success' : 'opacity-50'}>
            {category.quality_images_count}
          </span>
        )}
      </td>
      <td className="p-3">
        {showCreateForm ? (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
                placeholder="Category name..."
                className="flex-1 bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-sm"
              />
              <button
                onClick={handleCreate}
                disabled={!newCategoryName.trim() || isCreating}
                className="px-3 py-2 bg-success/20 text-success rounded-lg hover:bg-success/30 disabled:opacity-50"
              >
                {isCreating ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
              </button>
              <button
                onClick={() => setShowCreateForm(false)}
                className="px-3 py-2 bg-white/10 rounded-lg hover:bg-white/20"
              >
                <X size={16} />
              </button>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <button
              onClick={handleEnrich}
              disabled={isEnriching}
              className="px-3 py-2 bg-white/10 rounded-lg hover:bg-white/20 disabled:opacity-50"
              title="Fetch info from source"
            >
              {isEnriching ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />}
            </button>
            <select
              value={selectedCategoryId}
              onChange={(e) => setSelectedCategoryId(e.target.value)}
              className="flex-1 bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-sm text-white [&>option]:bg-gray-800 [&>option]:text-white"
            >
              <option value="">Select category...</option>
              {Array.isArray(aggCategories) && aggCategories.map(cat => (
                <option key={cat.id} value={cat.id}>
                  {cat.name_ru} ({cat.images_count || 0})
                </option>
              ))}
            </select>
            <button
              onClick={handleMap}
              disabled={!selectedCategoryId || isMapping}
              className="px-3 py-2 bg-primary/20 text-primary rounded-lg hover:bg-primary/30 disabled:opacity-50"
              title="Map to selected"
            >
              {isMapping ? <Loader2 size={16} className="animate-spin" /> : <Link size={16} />}
            </button>
            <button
              onClick={() => {
                if (!newCategoryName && displayName) {
                  setNewCategoryName(displayName)
                }
                setShowCreateForm(true)
              }}
              className="px-3 py-2 bg-success/20 text-success rounded-lg hover:bg-success/30"
              title="Create new category"
            >
              <Plus size={16} />
            </button>
          </div>
        )}
      </td>
    </tr>
  )
}

function CategoryRow({ category }) {
  return (
    <tr className="border-b border-white/10 hover:bg-white/5">
      <td className="p-3">
        <div className="font-medium">{category.name_ru}</div>
        <div className="text-sm opacity-60">{category.slug}</div>
      </td>
      <td className="p-3 text-center font-medium">{category.images_count || 0}</td>
      <td className="p-3 text-center">{category.published_count || 0}</td>
      <td className="p-3">
        {category.is_active ? (
          <span className="badge badge-approved">Active</span>
        ) : (
          <span className="badge badge-rejected">Inactive</span>
        )}
      </td>
    </tr>
  )
}

export default function Categories() {
  const queryClient = useQueryClient()
  const { showToast } = useToast()
  const [activeTab, setActiveTab] = useState('unmapped')
  const [qualityFilter, setQualityFilter] = useState(true)

  // Queries
  const { data: stats, isLoading: statsLoading } = useQuery(
    'categoryStats',
    categoriesAPI.getStats
  )

  const { data: aggCategories } = useQuery(
    'aggCategories',
    () => categoriesAPI.getAll({ active_only: true })
  )

  const { data: unmappedCategories, isLoading: unmappedLoading, refetch: refetchUnmapped } = useQuery(
    ['unmappedCategories', qualityFilter],
    () => categoriesAPI.getUnmapped({ quality_only: qualityFilter, limit: 100 }),
    { enabled: activeTab === 'unmapped' }
  )

  const { data: allCategories, isLoading: categoriesLoading } = useQuery(
    'allCategories',
    () => categoriesAPI.getAll({ active_only: false }),
    { enabled: activeTab === 'all' }
  )

  // Mutations
  const mapMutation = useMutation(categoriesAPI.mapSource, {
    onSuccess: () => {
      showToast('Category mapped successfully', 'success')
      queryClient.invalidateQueries('unmappedCategories')
      queryClient.invalidateQueries('categoryStats')
      queryClient.invalidateQueries('aggCategories')
    },
    onError: (err) => {
      showToast(`Failed to map: ${err.response?.data?.detail || err.message}`, 'error')
    }
  })

  const enrichMutation = useMutation(categoriesAPI.enrich, {
    onSuccess: (data) => {
      if (data.title) {
        showToast(`Found: ${data.title}`, 'success')
      } else if (data.status === 'not_found') {
        showToast('Category page not found on source site', 'warning')
      }
    },
    onError: (err) => {
      showToast(`Failed to fetch: ${err.response?.data?.detail || err.message}`, 'error')
    }
  })

  const createAndMapMutation = useMutation(categoriesAPI.createAndMap, {
    onSuccess: (data) => {
      showToast(`Created: ${data.category.name_ru}`, 'success')
      queryClient.invalidateQueries('unmappedCategories')
      queryClient.invalidateQueries('categoryStats')
      queryClient.invalidateQueries('aggCategories')
      queryClient.invalidateQueries('allCategories')
    },
    onError: (err) => {
      showToast(`Failed to create: ${err.response?.data?.detail || err.message}`, 'error')
    }
  })

  const handleRefresh = () => {
    refetchUnmapped()
    queryClient.invalidateQueries('categoryStats')
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">Categories</h1>
        <button
          onClick={handleRefresh}
          className="flex items-center gap-2 px-4 py-2 bg-white/10 rounded-lg hover:bg-white/20 transition-colors"
        >
          <RefreshCw size={18} />
          Refresh
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard
          icon={FolderTree}
          value={stats?.total_categories || 0}
          label="Total Categories"
          color="primary"
        />
        <StatCard
          icon={CheckCircle}
          value={stats?.active_categories || 0}
          label="Active"
          color="success"
        />
        <StatCard
          icon={Unlink}
          value={stats?.unmapped_source_categories || 0}
          label="Unmapped Sources"
          color="warning"
        />
        <StatCard
          icon={Link}
          value={stats?.mapped_source_categories || 0}
          label="Mapped Sources"
          color="secondary"
        />
      </div>

      {/* Tabs */}
      <div className="flex gap-4 mb-6">
        <button
          onClick={() => setActiveTab('unmapped')}
          className={`px-4 py-2 rounded-lg transition-colors ${
            activeTab === 'unmapped'
              ? 'bg-primary text-white'
              : 'bg-white/10 hover:bg-white/20'
          }`}
        >
          Unmapped ({stats?.unmapped_source_categories || 0})
        </button>
        <button
          onClick={() => setActiveTab('all')}
          className={`px-4 py-2 rounded-lg transition-colors ${
            activeTab === 'all'
              ? 'bg-primary text-white'
              : 'bg-white/10 hover:bg-white/20'
          }`}
        >
          All Categories ({stats?.total_categories || 0})
        </button>
      </div>

      {/* Content */}
      {activeTab === 'unmapped' && (
        <div className="glass p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">Unmapped Source Categories</h2>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={qualityFilter}
                onChange={(e) => setQualityFilter(e.target.checked)}
                className="w-4 h-4 rounded border-white/20 bg-white/10 text-primary focus:ring-primary"
              />
              <span className="text-sm">Only with quality images</span>
            </label>
          </div>

          {unmappedLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin opacity-50" />
            </div>
          ) : unmappedCategories?.length === 0 ? (
            <div className="text-center py-12 opacity-50">
              <CheckCircle className="w-12 h-12 mx-auto mb-4" />
              <p>All source categories are mapped!</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/20 text-left">
                    <th className="p-3">Source Category</th>
                    <th className="p-3">Source</th>
                    <th className="p-3 text-center">Images</th>
                    <th className="p-3 text-center">Quality</th>
                    <th className="p-3">Map To</th>
                  </tr>
                </thead>
                <tbody>
                  {unmappedCategories?.map(cat => (
                    <UnmappedCategoryRow
                      key={cat.id}
                      category={cat}
                      aggCategories={aggCategories}
                      onMap={mapMutation.mutateAsync}
                      onEnrich={enrichMutation.mutateAsync}
                      onCreateAndMap={createAndMapMutation.mutateAsync}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTab === 'all' && (
        <div className="glass p-6">
          <h2 className="text-lg font-semibold mb-4">All Aggregator Categories</h2>

          {categoriesLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin opacity-50" />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/20 text-left">
                    <th className="p-3">Category</th>
                    <th className="p-3 text-center">Images</th>
                    <th className="p-3 text-center">Published</th>
                    <th className="p-3">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {allCategories?.map(cat => (
                    <CategoryRow key={cat.id} category={cat} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
